import React from "react";
import styles from "./HistorialUser.module.css";
import axiosInstance from "../../api/axiosConfig";
import { Link, useNavigate, useLocation } from "react-router-dom";

/* ── Helpers ─────────────────────────────── */
function getInitials(name = "", lastName = "") {
  return `${name[0] || ""}${lastName[0] || ""}`.toUpperCase() || "?";
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" });
}

function getTipoBadge(tipo = "") {
  const t = tipo.toLowerCase();
  if (t.includes("online") || t.includes("tele") || t.includes("virtual"))
    return <span className={styles.badgeTele}>Teleconsulta</span>;
  if (t.includes("domicilio"))
    return <span className={styles.badgeDomicilio}>A domicilio</span>;
  return <span className={styles.badgePresencial}>Presencial</span>;
}

function getEstadoBadge(status = "") {
  const s = status.toLowerCase();
  if (s === "completado" || s === "completed" || s === "completada")
    return <span className={styles.badgeCompletada}>Completada</span>;
  if (s === "cancelado" || s === "cancelada" || s === "cancelled")
    return <span className={styles.badgeCancelada}>Cancelada</span>;
  if (s === "confirmado" || s === "confirmada")
    return <span className={styles.badgeConfirmada}>Confirmada</span>;
  return <span className={styles.badgePendiente}>Pendiente</span>;
}

/* ── Datos mock para desarrollo (cuando no hay token) ──── */
const MOCK_USER = {
  name: "Carlos",
  lastName: "García",
  email: "carlos.garcia@gmail.com",
  image: null,
  profileComplete: 70,
  OnlineShifts: [
    {
      id: 1,
      specialty: "Medicina General",
      professional_name: "Ana",
      professional_last_name: "Torres",
      date: "2026-03-18",
      time: "10:00 am",
      type: "Teleconsulta",
      status: "Confirmada",
    },
    {
      id: 2,
      specialty: "Pediatría",
      professional_name: "Luis",
      professional_last_name: "Mendoza",
      date: "2026-03-22",
      time: "3:30 pm",
      type: "Domicilio",
      status: "Pendiente",
    },
  ],
  pastShifts: [
    {
      id: 10,
      specialty: "Cardiología",
      professional_name: "Roberto",
      professional_last_name: "Vega",
      date: "2026-02-28",
      type: "Teleconsulta",
      status: "Completada",
    },
    {
      id: 11,
      specialty: "Dermatología",
      professional_name: "Patricia",
      professional_last_name: "Lima",
      date: "2026-02-16",
      type: "Domicilio",
      status: "Completada",
    },
    {
      id: 12,
      specialty: "Medicina General",
      professional_name: "Marco",
      professional_last_name: "Castro",
      date: "2026-01-30",
      type: "Teleconsulta",
      status: "Completada",
    },
  ],
  dependents: [
    { id: 1, name: "María", lastName: "García", relationship: "Cónyuge", age: 32, lastConsult: "2026-02-16" },
    { id: 2, name: "Diego", lastName: "García", relationship: "Hijo", age: 8, lastConsult: "2026-01-08" },
  ],
  lastConsult: { doctor: "Dr. Rojas", date: "28 Feb 2026" },
};

/* ── Íconos SVG ─────────────────────────── */
const BellIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const menuItems = [
  { label: "Resumen", path: "/mis-consultas" },
  { label: "Mis citas", path: "/mis-citas" },
  { label: "Historial médico", path: "/historial-medico" },
  { label: "Mis recetas", path: "/mis-recetas" },
  { label: "Dependientes", path: "/dependientes" },
  { label: "Mi perfil", path: "/mi-perfil" },
];

/* ══════════════════════════════════════════
   COMPONENTE PRINCIPAL
══════════════════════════════════════════ */
export default function HistorialUser() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = React.useState(null);
  const [token, setToken] = React.useState("");

  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  React.useEffect(() => {
    const t = localStorage.getItem("token");
    setToken(t || "");
    if (!t) {
      // Modo desarrollo: usar datos mock
      setUser(MOCK_USER);
    }
  }, []);

  React.useEffect(() => {
    if (!token) return;
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get("/api/datapersonal");
        setUser(response.data);
      } catch (error) {
        console.error("Error al obtener los detalles:", error);
        setUser(MOCK_USER); // fallback
      }
    };
    fetchData();
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  if (!user) return null;

  const initials = getInitials(user.name, user.lastName);
  const upcomingShifts = user.OnlineShifts || [];
  const pastShifts = user.pastShifts || [];
  const dependents = user.dependents || [];
  const profilePct = user.profileComplete || 70;
  const today = new Date().toLocaleDateString("es-PE", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div className={styles.panelPage}>

      {/* ══════════ SIDEBAR ══════════ */}
      <aside className={styles.sidebar}>
        {/* Info usuario */}
        <div>
          <div className={styles.sidebarTop}>
            {user.image ? (
              <img src={user.image} alt="avatar" className={styles.avatarImg} />
            ) : (
              <div className={styles.avatar}>{initials}</div>
            )}
            <p className={styles.userName}>{user.name} {user.lastName}</p>
            <p className={styles.userEmail}>{user.email}</p>
            {user.lastConsult && (
              <span className={styles.lastConsult}>
                Última: {user.lastConsult.doctor} · {user.lastConsult.date}
              </span>
            )}
          </div>

          {/* Menú */}
          <p className={styles.menuLabel}>Menú principal</p>
          <ul className={styles.menuList}>
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`${styles.menuItem} ${pathname === item.path ? styles.menuItemActive : ""}`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Cerrar sesión */}
        <div className={styles.sidebarBottom}>
          <button className={styles.btnLogout} onClick={handleLogout}>
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* ══════════ MAIN ══════════ */}
      <main className={styles.main}>

        {/* Header */}
        <div className={styles.mainHeader}>
          <div className={styles.mainHeaderLeft}>
            <p>Mi panel</p>
            <h1>Resumen</h1>
          </div>
          <div className={styles.mainHeaderRight}>
            <button className={styles.bellBtn}><BellIcon /></button>
            <div className={styles.headerAvatar}>{initials}</div>
          </div>
        </div>

        {/* Banner bienvenida */}
        <div className={styles.welcomeBanner}>
          <div className={styles.welcomeLeft}>
            <div className={styles.welcomeAvatar}>{initials}</div>
            <div>
              <p className={styles.welcomeEyebrow}>Bienvenido de vuelta</p>
              <p className={styles.welcomeName}>{user.name} {user.lastName}</p>
              <p className={styles.welcomeDate}>Hoy, {today}</p>
            </div>
          </div>
          <div className={styles.welcomeRight}>
            <p className={styles.profileLabel}>Perfil completo &nbsp; <strong style={{ color: "#2db899" }}>{profilePct}%</strong></p>
            <div className={styles.profileBar}>
              <div className={styles.profileBarFill} style={{ width: `${profilePct}%` }} />
            </div>
            <Link to="/mi-perfil" className={styles.profileLink}>Completar perfil →</Link>
          </div>
        </div>

        {/* Stats */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{upcomingShifts.length}</p>
            <p className={styles.statTitle}>Próximas citas</p>
            <p className={styles.statSub}>esta semana</p>
          </div>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{pastShifts.length}</p>
            <p className={styles.statTitle}>Consultas realizadas</p>
            <p className={styles.statSub}>historial total</p>
          </div>
          <div className={styles.statCard}>
            <p className={`${styles.statValue}`}>2</p>
            <p className={styles.statTitle}>Recetas activas</p>
            <p className={styles.statSub}>vigentes</p>
          </div>
          <div className={styles.statCard}>
            <p className={`${styles.statValue} ${styles.orange}`}>5</p>
            <p className={styles.statTitle}>Médicos favoritos</p>
            <p className={styles.statSub}>guardados</p>
          </div>
        </div>

        {/* Dos columnas: citas + acciones */}
        <div className={styles.twoCol}>

          {/* Próximas citas */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>Próximas citas</h2>
              <Link to="/mis-citas" className={styles.cardLink}>Ver todas →</Link>
            </div>
            {upcomingShifts.length === 0 ? (
              <p className={styles.emptyState}>No tenés citas programadas.</p>
            ) : (
              upcomingShifts.map((cita) => (
                <div key={cita.id} className={styles.citaItem}>
                  <div className={styles.citaAvatar}>
                    {getInitials(cita.professional_name || "", cita.professional_last_name || "")}
                  </div>
                  <div className={styles.citaInfo}>
                    <p className={styles.citaEspecialidad}>{cita.specialty}</p>
                    <p className={styles.citaDoctor}>
                      Dr/a. {cita.professional_name} {cita.professional_last_name}
                    </p>
                    <p className={styles.citaFecha}>{formatDate(cita.date)} · {cita.time}</p>
                  </div>
                  <div className={styles.citaMeta}>
                    {getTipoBadge(cita.type)}
                    {getEstadoBadge(cita.status)}
                    <span className={styles.cancelLink}>Cancelar cita</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Acciones rápidas */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>Acciones rápidas</h2>
            </div>
            <div className={styles.quickActions}>
              <Link to="/" className={`${styles.quickAction} ${styles.quickActionPrimary}`}>
                <span className={styles.quickActionTitle}>Nueva consulta</span>
                <span className={styles.quickActionSub}>Buscar especialista</span>
              </Link>
              <Link to="/mis-citas" className={styles.quickAction}>
                <span className={styles.quickActionTitle}>Mis citas</span>
                <span className={styles.quickActionSub}>Gestión de turnos</span>
              </Link>
              <Link to="/historial-medico" className={styles.quickAction}>
                <span className={styles.quickActionTitle}>Historial médico</span>
                <span className={styles.quickActionSub}>Consultas anteriores</span>
              </Link>
              <Link to="/mis-recetas" className={styles.quickAction}>
                <span className={styles.quickActionTitle}>Mis recetas</span>
                <span className={styles.quickActionSub}>Medicamentos activos</span>
              </Link>
              <Link to="/dependientes" className={styles.quickAction}>
                <span className={styles.quickActionTitle}>Dependientes</span>
                <span className={styles.quickActionSub}>Gestionar familiares</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Actividad reciente */}
        <div className={styles.card} style={{ marginBottom: 16 }}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>Actividad reciente</h2>
            <Link to="/historial-medico" className={styles.cardLink}>Ver historial completo →</Link>
          </div>
          {pastShifts.length === 0 ? (
            <p className={styles.emptyState}>No hay actividad reciente.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Especialidad</th>
                  <th>Médico</th>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {pastShifts.map((s) => (
                  <tr key={s.id}>
                    <td>{s.specialty}</td>
                    <td>Dr/a. {s.professional_name} {s.professional_last_name}</td>
                    <td>{formatDate(s.date)}</td>
                    <td>{getTipoBadge(s.type)}</td>
                    <td>{getEstadoBadge(s.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Mis dependientes */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>Mis dependientes</h2>
            <Link to="/dependientes" className={styles.cardLink}>Gestionar →</Link>
          </div>
          <div className={styles.dependientesGrid}>
            {dependents.map((dep) => (
              <div key={dep.id} className={styles.dependienteCard}>
                <div className={styles.depAvatar}>{getInitials(dep.name, dep.lastName)}</div>
                <div>
                  <p className={styles.depName}>{dep.name} {dep.lastName}</p>
                  <p className={styles.depMeta}>{dep.relationship} · {dep.age} años</p>
                  {dep.lastConsult && (
                    <p className={styles.depMeta}>Última: {formatDate(dep.lastConsult)}</p>
                  )}
                </div>
              </div>
            ))}
            <Link to="/registrar-usuario" className={styles.btnAddDep}>
              + Agregar dependiente
            </Link>
          </div>
        </div>

      </main>
    </div>
  );
}
